import React from "react";
import { has, get } from "lodash";
import PropTypes from "prop-types";

import { withStyles } from "@mui/styles";
import {
  MenuItem,
  FormControl,
  FormHelperText,
  Typography,
  Select,
  Button,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { FORM_SUBMISSION_ERROR, PREFERRED_LANGS } from "common/constants";
import Yup from "yup";
import { Form, Formik } from "formik";
import { accessLevels } from "./options";
import SimpleSnackbar from "common/components/SimpleSnackbar";
import { hasError, getError } from "common/helpers/formik";

const schema = Yup.object().shape({
  email: Yup.string().email().required("Email is required"),
  firstName: Yup.string().required("First name is required"),
  phone: Yup.string(),
  langPref: Yup.string().required("Language Preference is required"),
  // .required("Phone number is required")
  // .matches(
  //   /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/,
  //   "Phone number is not valid"
  // ),
});

const UserForm = ({ classes, handleSubmit, initialValues, isEdit }) => (
  <Formik
    enableReinitialize
    initialValues={initialValues}
    onSubmit={handleSubmit}
    validationSchema={schema}
  >
    {({ values, errors, handleChange, isSubmitting, status, touched }) => {
      const checkError = hasError(errors, touched);
      const errorMsg = getError(errors, touched);

      return (
        <Form>
          <Grid container direction="column" spacing={2}>
            <Grid size={{ xs: 4 }}>
              <FormControl className={classes.formControl}>
                <TextField
                  error={checkError("email")}
                  id="email"
                  type="email"
                  label="Email Address"
                  margin="normal"
                  helperText={errorMsg("email")}
                  onChange={handleChange}
                  value={values.email}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <FormControl className={classes.formControl}>
                <TextField
                  error={checkError("firstName")}
                  id="firstName"
                  type="text"
                  label="First Name"
                  margin="normal"
                  helperText={errorMsg("firstName")}
                  onChange={handleChange}
                  value={values.firstName}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <FormControl className={classes.formControl}>
                <TextField
                  error={checkError("lastName")}
                  id="lastName"
                  type="text"
                  label="Last Name"
                  margin="normal"
                  helperText={errorMsg("lastName")}
                  onChange={handleChange}
                  value={values.lastName}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <FormControl className={classes.formControl}>
                <TextField
                  error={checkError("password")}
                  id="password"
                  type="password"
                  label="Password"
                  margin="normal"
                  helperText={errorMsg("password")}
                  onChange={handleChange}
                  value={values.password}
                />
                <Typography variant="caption">
                  Must be at least 8 characters long with 1 uppercase, number,
                  or special character.
                </Typography>
              </FormControl>
            </Grid>
            {/* <Grid size={{ xs: 4 }}>
              <FormControl className={classes.formControl}>
                <TextField
                  error={checkError("phone")}
                  id="phone"
                  type="text"
                  label="Phone"
                  margin="normal"
                  helperText={errorMsg("phone")}
                  onChange={handleChange}
                  value={values.phone}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">+1</InputAdornment>
                    ),
                  }}
                />
                <Typography variant="caption">
                  Please enter phone number without the country code
                </Typography>
              </FormControl>
            </Grid> */}
            <Grid size={{ xs: 4 }}>
              <FormControl
                className={classes.formControl}
                error={checkError("accessLevel")}
              >
                <Select
                  value={values.accessLevel}
                  onChange={handleChange}
                  name="accessLevel"
                  id="accessLevel"
                  error={checkError("accessLevel")}
                >
                  {accessLevels.map((accessLevel) => (
                    <MenuItem
                      value={accessLevel.value}
                      key={accessLevel.name}
                      disabled={
                        accessLevel.value === "companykeycontact" &&
                        values.is_forms_allowed === false
                      }
                    >
                      {accessLevel.name}
                    </MenuItem>
                  ))}
                </Select>
                {checkError("accessLevel") ? (
                  <FormHelperText>{errorMsg("accessLevel")}</FormHelperText>
                ) : (
                  <FormHelperText>Access Level</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <FormControl
                className={classes.formControl}
                error={checkError("langPref")}
              >
                <Select
                  value={values.langPref || PREFERRED_LANGS[0].value}
                  onChange={handleChange}
                  name="langPref"
                  id="langPref"
                  error={checkError("langPref")}
                >
                  {PREFERRED_LANGS.map((langPref) => (
                    <MenuItem value={langPref.value} key={langPref.name}>
                      {langPref.name}
                    </MenuItem>
                  ))}
                </Select>
                {checkError("langPref") ? (
                  <FormHelperText>{errorMsg("langPref")}</FormHelperText>
                ) : (
                  <FormHelperText>Preferred Language</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.button}
                disabled={isSubmitting}
              >
                Save User
              </Button>
              {has(errors, "http") && (
                <SimpleSnackbar message={FORM_SUBMISSION_ERROR} />
              )}
            </Grid>
          </Grid>
          {get(status, "success") ? (
            <SimpleSnackbar message="User saved!" />
          ) : null}
        </Form>
      );
    }}
  </Formik>
);

const styles = (theme) => ({
  button: {
    marginTop: theme.spacing(2),
  },
  paper: {
    paddingTop: 16,
    paddingBottom: 16,
    marginTop: theme.spacing(3),
    width: "80%",
  },
  formControl: {
    margin: theme.spacing(1),
    width: "100%",
  },
});

UserForm.propTypes = {
  classes: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default withStyles(styles)(UserForm);
